// import { useIdleTimer } from "react-idle-timer";
import axios from "axios";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";

import { clearReduxStoreAction, updateCustomerList, updateLocationList, updateOpenCount, updateWindowWidth } from "../../../appRedux/reducers/Common";
import LocalStorageUtil from "../../../util/LocalStorageUtil";
import { LocalStorageConstants, LocalStorageValueConstants } from "../../../constants/ApplicationConstants/LocalStorageConstants";
import { Base64StringEncoding } from "../../../util/Cryptography";
import Authentication from "../../../util/Authentication";
import HttpCodeConstants from "../../../constants/HttpConstants/HttpCodeConstants";
import { CustomNotification } from "../../../util/CustomNotification";
import * as authService from "../../../services/authService";
import * as masterServices from "../../../services/masterServices";
import * as commonAPI from "../../../services/commonServices";
import { ModuleAndIdMapping, Modules, Operations } from "../../../constants/DatabaseConstants/MasterConstant";
import useValue from "../../../hooks/useValue";
import UIConfiguration from "../../../util/UIConfiguration";
import { RouteConstants } from "../../../constants/ApplicationConstants/RouteConstants";

import type { RootState } from "../../../appRedux/store";
import type { UserInterface } from "../../../shared/interface/auth/User.interface";
import { getAllOpenSupportQueryCount } from "../../../services/supportQuery";

const getDifferenceInMiliSeconds = () => {
  const currentTimeStamp = moment();
  const tokenExpiryTimeStamp: string = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.TOKEN_EXPIRY_TIMESTAMP) ?? "{}";
  const differenceInTime = moment(tokenExpiryTimeStamp).diff(currentTimeStamp);
  const differenceInMiliSeconds = moment.duration(differenceInTime).asMilliseconds();
  return differenceInMiliSeconds;
};

export const useMainApp = () => {
  /* CONSTANTS */
  const dispatch = useDispatch();

  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  /*  STATE */
  const { navStyle } = useSelector(({ settings }: RootState) => settings);
  const [loadApp, setLoadApp] = useState(getDifferenceInMiliSeconds() > 0 ? false : true);
  const [refresh, setRefresh] = useState(1);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(true);

  /* HANDLER */
  const handleLoadApp = (val: boolean) => {
    setLoadApp(val);
  };

  // useIdleTimer({
  //   onIdle: async () => {
  //     /* dont clear local storage here as a flag is set to prompt user on sign-in page about session expiry */
  //     await authService.signOutAPI();
  //     LocalStorageUtil.localStorageSetItem(LocalStorageConstants.IS_USER_ACTIVE, LocalStorageValueConstants.FALSE);
  //     /* source tab cannot detect localstorage changes so we have to manually move to signIn page (check more on how storage events work) */
  //     window.location.assign(RouteConstants.ROOT_PAGE);
  //   },
  //   crossTab: true,
  //   timeout: process.env.REACT_APP_SESSION_TIMEOUT ? +process.env.REACT_APP_SESSION_TIMEOUT : 900_000
  // });

  // EFFECTS

  /* event listener added to move to sign-in page when user session expires. */
  useEffect(() => {
    const fn = (e: StorageEvent) => {
      const childFn = async () => {
        /* we only want to change urls when token or when user becomes idle */
        /* e.key when null means clear local storage event fired. */
        if (e.key === Base64StringEncoding.encode(LocalStorageConstants.IS_TOKEN_EXPIRED) || e.key === Base64StringEncoding.encode(LocalStorageConstants.IS_USER_ACTIVE) || !e.key) {
          const userSession = Authentication.isUserSessionActive();
          if (!userSession) window.location.assign("/");
        }
      };

      childFn();
    };

    window.addEventListener("storage", fn, false);
    return () => window.removeEventListener("storage", fn, false);
  }, []);

  const clearSessionAndRedirect = async () => {
    try {
      await authService.signOutAPI();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      localStorage.clear();
      dispatch(clearReduxStoreAction());
      window.location.assign(RouteConstants.ROOT_PAGE);
    }
  };

  useEffect(() => {
    /* calculate difference between two timestamps (expiry timestamp and current timestamp) */
    const currentTimeStamp = moment();
    const tokenExpiryTimeStamp: string | null = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.TOKEN_EXPIRY_TIMESTAMP);
    const differenceInTime = moment(tokenExpiryTimeStamp).diff(currentTimeStamp);
    const differenceInMiliSeconds = moment.duration(differenceInTime).asMilliseconds();

    /* on token expiry we don't want to directly prompt the user back to the signIn page, But we will use refresh mechanism just before the expiry time of the token
       as soon as the token is about to expire this callback fn registered with the timer will execute and get the new token.
       the expiry time of token and token is updated in localstorage.
    */

    const timer = setTimeout(async () => {
      try {
        handleLoadApp(true);
        await authService.refreshTokenAPI();
        Authentication.addExpiryTimeToLocalStorage();

        /* we will keep updating after every execution so the timer is registered for the next cycle as well.  */
        setRefresh(Math.random());
      } catch (error) {
        /* source tab cannot detect localstorage changes so we have to manually move to signIn page (check more on how storage events work) */
        await clearSessionAndRedirect();
      } finally {
        handleLoadApp(false);
      }
    }, differenceInMiliSeconds);

    /* cleanup function which will clear old timers */
    return () => clearTimeout(timer);
  }, [refresh]);

  /* axios-interceptor */
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;

      if (status === HttpCodeConstants.INVALID_TOKEN) {
        /* clear cookies, clear localstorage and go to root page. */
        localStorage.clear();
        dispatch(clearReduxStoreAction());
        window.location.assign(RouteConstants.ROOT_PAGE);
      } else if (status === HttpCodeConstants.USER_SESSION_DOES_NOT_EXIST || status === HttpCodeConstants.NO_ROLE_ASSIGNED || status === HttpCodeConstants.WEB_USER_INACTIVE) {
        /* clear cookies, clear localstorage and go to root page. */
        await clearSessionAndRedirect();
      }

      return Promise.reject(error);
    }
  );

  /* EFFECTS */
  useEffect(() => {
    window.addEventListener("resize", () => {
      dispatch(updateWindowWidth(window.innerWidth));
    });
  }, [dispatch]);

  /* get Topbar filters list */
  useEffect(() => {
    const getCustomerListAndUpdateState = async () => {
      try {
        handleEnableLoading();
        /* getCustomerMasterList */
        const customerListResponse = await masterServices.getOneMasterListById(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);
        dispatch(updateCustomerList(customerListResponse?.["AllRecordsList"]));

        if (masterFilters?.customerId) {
          /* getCustomerLocationMasterList */
          const locationListResponse = await commonAPI.getLocationListByCustomerId(masterFilters?.customerId);
          dispatch(updateLocationList(locationListResponse?.data));
        }
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableLoading();
      }
    };
    getCustomerListAndUpdateState();
  }, []);

  /* Update support-query openCount*/
  useEffect(() => {
    const fetchSupportQueryData = async () => {
      try {
        const response = await getAllOpenSupportQueryCount();
        const openCountFromAPI = response?.data?.openCount ?? 0;

        dispatch(updateOpenCount(openCountFromAPI));
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      }
    };

    if (Authentication.hasModuleAndOperationAccess(userData, Modules.SUPPORT_QUERY, Operations.READ)) {
      fetchSupportQueryData();
    }
  }, []);

  /* MEMO */
  useMemo(() => {
    if (!userData?.config?.theme) return;

    UIConfiguration.updateUIConfig(userData);
  }, [userData?.config?.theme]);

  return { navStyle, loading: loadApp || loading, userData };
};
